import React, { useState, useEffect } from "react";
import api from '../Services/api';
import { useNavigate } from "react-router-dom";

interface ContactSubmission {
  _id: string;
  name: string;
  email: string;
  category: string;
  message: string;
  status: string;
  createdAt: string;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalSubmissions: number;
  submissionsPerPage: number;
}

export const ContactFormsList = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState<PaginationData | null>(null);
  const [limit, setLimit] = useState<number>(10);
  const [nameSearch, setNameSearch] = useState<string>("");
  const navigate = useNavigate();

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await api.get(`/contact`, {
        params: {
          page,
          limit,
          nameSearch
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(data);
      setPaginationData(data);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchSubmissions();
  }, [page, limit, nameSearch]);

  const handleStatusChange = async (submissionId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem("token");
      await api.patch(
        `/contact/${submissionId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSubmissions();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Contact Form Submissions</h2>

      <div className="mb-4 flex gap-4 items-center">
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="p-2 border rounded bg-white dark:bg-neutral-dark text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
        >
          <option value={10}>10 per page</option>
          <option value={20}>20 per page</option>
          <option value={50}>50 per page</option>
        </select>

        <input
          type="text"
          placeholder="Search by name"
          value={nameSearch}
          onChange={(e) => setNameSearch(e.target.value)}
          className="p-2 border rounded bg-white dark:bg-neutral-dark text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
        />
      </div>

      {loading ? (
        <div className="text-gray-900 dark:text-gray-100">Loading submissions...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-neutral-dark shadow-md rounded-lg">
              <thead className="bg-gray-50 dark:bg-neutral-dark">
                <tr>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Date</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Name</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Email</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Category</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Status</th>
                  <th className="px-6 py-3 text-left text-gray-900 dark:text-gray-100">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-neutral-dark divide-y divide-gray-200 dark:divide-gray-700">
                {submissions?.length > 0 ? (
                  submissions.map((submission) => (
                    <tr key={submission._id}>
                      <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                        {new Date(submission.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                        {submission.name}
                      </td>
                      <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                        {submission.email}
                      </td>
                      <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                        {submission.category}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={submission.status}
                          onChange={(e) => handleStatusChange(submission._id, e.target.value)}
                          className="p-1 border rounded bg-white dark:bg-neutral-dark text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                        >
                          <option value="new">New</option>
                          <option value="in-progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => navigate(`/admin/submissions/${submission._id}`, { state: { submission } })}
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-900 dark:text-gray-100">
                      No submissions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationData && (
            <div className="mt-4 flex justify-between items-center text-gray-900 dark:text-gray-100">
              <div>
                Page {paginationData.currentPage} of {paginationData.totalPages}
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-gray-200 dark:bg-neutral-light text-gray-900 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === paginationData.totalPages}
                  className="px-4 py-2 bg-gray-200 dark:bg-neutral-light text-gray-900 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
